// Thin wrapper around /api/ai/* that:
//   - generates a stable Idempotency-Key per logical call (so retries don't double-bill)
//   - surfaces HTTP 402 (budget exhausted) as a typed error the UI can intercept
//   - returns parsed JSON on success
//
// Subscribers to the budget event get a clear "AI budget hit" modal — see
// useAiBudget() in this module.

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

export class AiBudgetExhaustedError extends Error {
  constructor(payload) {
    super('AI budget exhausted');
    this.name = 'AiBudgetExhaustedError';
    this.resetsAt = payload?.resetsAt || null;
    this.budget = payload?.budget;
    this.used = payload?.used;
  }
}

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Global budget-event bus. UI components register a listener; aiClient fires
// an event when any /api/ai/* call returns 402 so the editor sees the modal
// immediately, regardless of which tool triggered it.
const budgetListeners = new Set();
let currentBudget = null; // { resetsAt, budget, used }

export function onAiBudgetExhausted(listener) {
  budgetListeners.add(listener);
  return () => budgetListeners.delete(listener);
}

export function getCurrentBudgetState() {
  return currentBudget;
}

export function clearBudgetState() {
  currentBudget = null;
  for (const l of budgetListeners) l(null);
}

function fireBudgetExhausted(payload) {
  currentBudget = payload;
  for (const l of budgetListeners) l(payload);
}

async function aiCall(path, body, { idempotencyKey } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  headers['Idempotency-Key'] = idempotencyKey || newIdempotencyKey();

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body || {}),
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (res.status === 402) {
    fireBudgetExhausted(data);
    throw new AiBudgetExhaustedError(data);
  }
  if (!res.ok) {
    const err = new Error(data?.error || `AI call failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const aiClient = {
  rewrite: (body, opts) => aiCall('/ai/rewrite', body, opts),
  rewriteBench: (body, opts) => aiCall('/ai/rewrite-bench', body, opts),
  verify: (body, opts) => aiCall('/ai/verify', body, opts),
  overview: (body, opts) => aiCall('/ai/overview', body, opts),
  redThread: (body, opts) => aiCall('/ai/red-thread', body, opts),
  changeSummary: (body, opts) => aiCall('/ai/change-summary', body, opts),
  scrape: (body, opts) => aiCall('/ai/scrape', body, opts),
};
